import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { dateTime, panamaDay, tcnApi } from './api';

export function backfillDays(from, to) {
    const valid = (day) => /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(Date.parse(`${day}T00:00:00Z`))
        && new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) === day;
    return valid(from) && valid(to) ? (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000 + 1 : 0;
}

export default function TcnSyncStatus({ status, isAdmin, busy, onBackfillStarted }) {
    const [visible, setVisible] = useState(false);
    const [range, setRange] = useState({ from: '', to: '' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    if (!status) return null;
    const lastError = ['error', 'failed'].includes(status.lastCompletedRun?.status) ? status.lastCompletedRun : null;
    const missing = !status.lastSuccessfulSyncAt;
    const needsBackfill = status.isStale || lastError || missing;
    const days = backfillDays(range.from, range.to);
    const validRange = days >= 1 && days <= 90;
    const blocked = busy || Boolean(status.activeRun);

    function open() {
        const today = panamaDay(status.checkedAt ? new Date(status.checkedAt) : new Date());
        setRange({ from: status.lastSuccessfulSyncAt ? panamaDay(new Date(status.lastSuccessfulSyncAt)) : today, to: today });
        setError('');
        setVisible(true);
    }
    async function submit(event) {
        event.preventDefault();
        if (!isAdmin || blocked || submitting || !validRange) return;
        setSubmitting(true);
        setError('');
        try {
            const result = await tcnApi('backfill', { method: 'POST', data: range });
            if (!result?.accepted || !result.runId) throw new Error('No se pudo iniciar el backfill. Revisa el historial.');
            onBackfillStarted(result.runId, range, days);
            setVisible(false);
        } catch (failure) {
            setError(failure.status === 409 ? 'Ya hay una sincronización o backfill en curso. Espera a que termine.'
                : failure.status === 429 ? 'Se alcanzó el presupuesto diario o el límite de llamadas del proveedor. Revisa el historial antes de volver a intentar.'
                : failure.message);
        } finally { setSubmitting(false); }
    }

    return <section className="tcn-sync-status" aria-label="Estado de sincronización">
        <p>Último sync exitoso: <strong>{status.lastSuccessfulSyncAt ? dateTime(status.lastSuccessfulSyncAt) : 'Sin registro disponible'}</strong> · Hora de Panamá</p>
        {!status.syncEnabled && <div role="status"><Message severity="info" text="Sincronización automática desactivada" /></div>}
        {needsBackfill && <div className="tcn-sync-warning" role="alert">
            {status.isStale && <p>El último sync exitoso tiene más de 12 horas. Puede haber ventas pendientes de recuperar.</p>}
            {lastError && <p>La última ejecución terminó en error: <strong>{lastError.errorMessage || lastError.errorCode || 'Error sin detalle disponible.'}</strong></p>}
            {missing && <p>No hay un sync exitoso en el historial disponible.</p>}
            {isAdmin && <Button label="Rellenar hueco" icon="pi pi-calendar-plus" onClick={open} disabled={blocked} />}
        </div>}
        {status.activeRun && <p role="status">{status.activeRun.trigger === 'backfill' ? 'Backfill' : 'Sincronización'} en curso. Espera a que termine antes de iniciar otra ejecución.</p>}
        {isAdmin && <Dialog header="Rellenar hueco" visible={visible} modal className="tcn-backfill-dialog" style={{ width: '32rem', maxWidth: '95vw' }}
            onHide={() => !submitting && setVisible(false)} closable={!submitting} closeOnEscape={!submitting}>
            <form onSubmit={submit}>
                <p>Recuperar ventas de todas las máquinas, día por día, en horario de Panamá. Máximo 90 días por solicitud.</p>
                {missing && <Message severity="info" text="No hay fecha de referencia. El rango comienza hoy; ajústalo al período que necesitas recuperar." />}
                <div className="tcn-backfill-fields">
                    <label htmlFor="tcn-backfill-from">Desde<InputText id="tcn-backfill-from" type="date" value={range.from} required disabled={submitting} onChange={(event) => setRange({ ...range, from: event.target.value })} /></label>
                    <label htmlFor="tcn-backfill-to">Hasta<InputText id="tcn-backfill-to" type="date" value={range.to} required disabled={submitting} onChange={(event) => setRange({ ...range, to: event.target.value })} /></label>
                </div>
                {!validRange && <div role="alert"><Message severity="warn" text={days > 90 ? 'El rango supera 90 días. Selecciona un tramo de hasta 90 días y luego continúa con el siguiente.' : 'Selecciona fechas válidas y una fecha final igual o posterior a la inicial.'} /></div>}
                {error && <div role="alert"><Message severity="error" text={error} /></div>}
                {blocked && <Message severity="info" text="Hay una ejecución en curso. Espera a que termine." />}
                <div className="tcn-backfill-actions">
                    <Button label="Cancelar" type="button" className="p-button-text" disabled={submitting} onClick={() => setVisible(false)} />
                    <Button label="Iniciar backfill" type="submit" loading={submitting} disabled={!validRange || blocked} />
                </div>
            </form>
        </Dialog>}
    </section>;
}
